import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { LucideIcon } from './LucideIcon';
import type { Color as Interface } from '../../design/components';

export const Color: React.FC<Interface> = ({
  name,
  description,
  state,
  placeholder = 'Select a color',
  hidden,
  inline,
  readonly,
  disabled,
  required,
  error,
}) => {
  const [value, setValue] = state || ['', () => {}];

  if (inline) {
    return value ? (
      <span
        className={cn('inline-block w-5 h-5 rounded border', hidden && 'hidden')}
        style={{ backgroundColor: value }}
      />
    ) : null;
  }

  return (
    <div className={cn('flex flex-col gap-2', hidden && 'hidden')} data-disabled={disabled}>
      {name && (
        <Label htmlFor={name} className={cn(error && 'text-destructive')}>
          {name}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      <div className="relative flex items-center">
        <div
          className={cn(
            'flex w-full items-center rounded-md border bg-background px-3 py-2 h-9 gap-2',
            'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
            error && 'border-destructive',
            disabled && 'cursor-not-allowed opacity-50',
            readonly && 'bg-muted'
          )}
        >
          <input
            type="color"
            value={value || '#000000'}
            onChange={(e) => setValue(e.target.value)}
            disabled={disabled || readonly}
            className={cn(
              'w-6 h-6 p-0 border-0 bg-transparent cursor-pointer rounded',
              '[&::-webkit-color-swatch-wrapper]:p-0',
              '[&::-webkit-color-swatch]:border [&::-webkit-color-swatch]:border-border [&::-webkit-color-swatch]:rounded',
              (readonly || disabled) && 'cursor-default'
            )}
          />
          <Input
            id={name}
            name={name}
            type="text"
            value={value || ''}
            placeholder={placeholder}
            readOnly
            disabled={disabled}
            className="flex-1 border-0 shadow-none focus-visible:ring-0 h-auto p-0"
          />
          {value && !readonly && !disabled && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => setValue('')}
            >
              <LucideIcon name="X" size="small" />
            </Button>
          )}
        </div>
      </div>

      {(error || description) && (
        <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
          {error || description}
        </p>
      )}
    </div>
  );
};
